import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip, MenuItem } from '@mui/material';
import {POSITION_MODEL} from "../../Helpers/PositionModel";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * DropDownMenuItem renders a menu item with an optional tooltip.
 *
 * @param {Object} props - Component properties.
 * @param {string} [props.url] - URL to navigate when the item is clicked.
 * @param {React.ReactNode|string} props.icon - Icon to display. If a string is provided, it will be used as a CSS class.
 * @param {string} props.name - Translation key for the menu item text.
 * @param {Function} [props.handleClose=() => {}] - Callback to close the menu.
 * @param {Function|null} [props.click=null] - Optional additional click handler.
 * @param {string} [props.id='nav-bar-menu'] - Identifier used for element IDs.
 * @param {number} props.index - Index of the menu item.
 * @param {boolean} [props.showTooltip=false] - If true, displays a tooltip.
 * @param {string|null} [props.tooltipText=null] - Translation key for tooltip text. Defaults to `name` if not set.
 * @param {string} [props.fontColor='font-color-general-950'] - CSS class for font color.
 * @param {string} [props.iconColor='icon-color-general-950'] - CSS class for icon color.
 * @param {boolean} [props.disabled=false] - Disables the menu item if true.
 * @param {React.ReactNode} [props.children] - Optional children to render instead of default layout.
 * @returns {JSX.Element}
 */
const DropDownMenuItem = (props) => {
    const {
        url,
        icon,
        name,
        handleClose = () => {},
        click = null,
        id = "nav-bar-menu",
        index,
        showTooltip = false,
        tooltipText = null,
        fontColor= 'font-color-general-950',
        iconColor='icon-color-general-950',
        disabled = false,
        children,
        iconPosition=POSITION_MODEL.RIGHT
    } = props;

    const { t } = useTranslation();
    const navigate = useNavigate();
    const isClickable = click || url;
    const tooltipContent = tooltipText ? t(tooltipText) : t(name);

    const handleClick = () => {
        if (isClickable) {
            if (click) click();
            if (url) {
                if (typeof url === 'string') {
                    navigate(url);
                } else if (typeof url === 'object' && url.pathname) {
                    navigate(url.pathname, { state: url.state });
                }
            }
            handleClose();
        }
    };


    /**
     * Returns the formatted icon element based on its type and configured position.
     *
     * This helper builds the container classes dynamically depending on whether the
     * icon should be aligned to the left or right. It supports three display cases:
     * 1. When the icon is a numeric string, it renders a simple <span>.
     * 2. When the icon is a non-numeric string, it renders a CSS-based SVG icon.
     * 3. When the icon is a React node, it renders the node directly.
     *
     * @function
     * @returns {JSX.Element} A JSX element containing the properly styled icon.
     */
    const getIcon = () => {
        const classes = iconPosition === POSITION_MODEL.RIGHT ? `justify-content-end ${name ? 'ml-3 ':''}` : `justify-content-start ${name ? 'mr-3':''}`;

        return <div className={`${classes} d-flex align-items-center ${iconColor}`}>
            {typeof icon === 'string' && !isNaN(icon) ? (
                <span>{icon}</span>
            ) : typeof icon === 'string' ? (
                <div className={`icon-svg-black ${icon}`} />
            ) : (
                icon
            )}
        </div>
    }

    const menuItemContent = (
        <div id={`link-${id}${index}`} className="w-100">
            <div id={`${id}${index}`}>
                <div className={`ibl-paragraph ibl-font-w-600 d-flex align-items-center w-100 ${ iconPosition === POSITION_MODEL.RIGHT ? "justify-content-between" : ""}`}>
                    {iconPosition === POSITION_MODEL.LEFT && getIcon()}
                    {name && <span className={`${fontColor}`}>{t(name)}</span>}
                    {iconPosition === POSITION_MODEL.RIGHT && getIcon()}
                </div>
            </div>
        </div>
    );

    return (
        <MenuItem
            key={index}
            disabled={disabled}
            onClick={
                isClickable
                    ? (e) => {
                          e.stopPropagation();
                          handleClick?.(e);
                      }
                    : undefined
            }
            className={isClickable ? '' : 'disabled-menu-item'}
        >
            {
                children ?
                    children
                :
                showTooltip ? (
                    <Tooltip disableInteractive title={tooltipContent} arrow placement='right'>
                        <div>{menuItemContent}</div>
                    </Tooltip>
                ) : (
                    menuItemContent
                )
            }

        </MenuItem>
    );
};

export default DropDownMenuItem;
